import { DetailText } from '@utilitywarehouse/hearth-react';

import { FAQTopic } from '@components/FAQ';

import { Sections, Topics } from './constants';

const ORDERING_TOPICS: FAQTopic<Topics>[] = [
  {
    id: Topics.multipleCards,
    question: 'Can I get more than one Cashback Card?',
    answer: (
      <>
        Yes, you can add another cardholder for free so you can both earn money
        off your UW bill. You can do this when you enter your details before
        signing up, or later in your online account.
      </>
    ),
  },
  {
    id: Topics.monthlyFee,
    question: 'Why is there a £3 monthly fee?',
    answer: (
      <>
        The £3 charge is a monthly management fee all our customers pay, which
        helps us continue to offer you great cashback rewards.
      </>
    ),
  },
  {
    id: Topics.creditScore,
    question: 'Will ordering a Cashback Card impact my credit score?',
    answer: <>No, you won’t be credit checked by ordering a Cashback Card.</>,
  },
];

const SPENDING_TOPICS: FAQTopic<Topics>[] = [
  {
    id: Topics.cashbackEarnt,
    question: 'Where can I see how much cashback I’ve earnt?',
    answer: (
      <>
        You can see how much cashback you’ve earnt in realtime on the UW app.
        With our Cashback Insights feature, you can view your all-time earnings,
        average monthly cashback, and top five retailers you’ve earned cashback
        with.
      </>
    ),
  },
  {
    id: Topics.mobilePay,
    question: 'Will my Cashback Card work on Apple Pay or Google Pay?',
    answer: (
      <>
        Yes, you can set up your Cashback Card on Apple Pay or Google Pay. This
        gives you an easy, secure, and private way to pay in-store, online, and
        even in your favourite apps.
      </>
    ),
  },
  {
    id: Topics.useAbroad,
    question: 'Can I use my Cashback Card abroad?',
    answer: (
      <>
        Yes, you can use your Cashback Card abroad anywhere that accepts
        Mastercard, and we’ll refund foreign exchange fees back on your next
        monthly bill.
      </>
    ),
  },
  {
    id: Topics.retailers,
    question: 'How often do you add more retailers?',
    answer: (
      <>
        We’re always on the lookout for more retailer partners! We have over 55
        and the list is ever-growing. When a new partner is added, we’ll let you
        know in the UW app.
      </>
    ),
  },
  {
    id: Topics.cancelation,
    question: 'Can I cancel at any time?',
    answer: (
      <>Yes - you can cancel your Cashback Card at any time by calling us.</>
    ),
  },
];

const EARNINGS_TOPICS: FAQTopic<Topics>[] = [
  {
    id: Topics.cashbackEveryTime,
    question: 'Will I earn cashback every time I use my card?',
    answer: (
      <>
        You can use your Cashback Card anywhere that accepts Mastercard, and
        you’ll earn the best cashback rates by shopping with our retail
        partners. You can earn 1% cashback everywhere else, up to a maximum of
        £5, £10 or £15 per month, depending on the number of qualifying bundle
        services you have with us (energy, broadband, mobile, insurance):
        <br />
        <br />
        <ul>
          <li>
            If you have one live bundle service with UW, you’ll earn 1% on your
            first £500 of spend up to a maximum of £5 per month.
          </li>
          <li>
            If you have two or three live bundle services with UW, you’ll earn
            1% on your first £1,000 of spend up to a maximum of £10 per month.
          </li>
          <li>
            If you have four or more live bundle services with UW, you’ll earn
            1% on your first £1,500 of spend up to a maximum of £15 per month.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: Topics.monthlyLimit,
    question: '*How much monthly cashback can I earn?',
    answer: (
      <>
        Customers who regularly use their Cashback Card save an average £178
        each year off their UW bills, so £14.83 a month.¹ Top cardholders earn
        on average £461 cashback each year, so £38.42 a month.²
        <br />
        <br />
        <DetailText size="sm">
          ¹Between 2024-01-01 and 2024-12-29 (52 weeks), 17,771 Wallets earned
          CBC at least once each of those weeks. Average cashback for those
          wallets £214.67. £36 annual fees subtracted - £178.67. One time
          application fee of £5 subtracted = £173.67. Magic excluded, including
          only positive cashback transactions.
          <br />
          <br />
          ²Calculated using the average cashback earned between 01-01-2024 to
          29-12-2024 (52 weeks) by the top 10% of active account holders
          (customers who used their card at least once a week during this
          period). This figure excludes customers who won 100% cashback through
          the Cashback Magic promotion.
        </DetailText>
      </>
    ),
  },
  {
    id: Topics.cashbackToBankAccount,
    question:
      'Can I take the cashback as money in my bank account, rather than money off my UW bill?',
    answer: <>Your cashback is always credited directly to your UW bill. </>,
  },
  {
    id: Topics.cashbackWithdraw,
    question: 'Can I withdraw money that I put on my Cashback Card?',
    answer: (
      <>
        Yes, you can withdraw money from your Cashback Card via an ATM or
        directly to your bank account using our app. Please note, there’s a 50p
        charge to withdraw cash from an ATM, and there may be additional charges
        depending on the ATM provider.
      </>
    ),
  },
];

const SECURITY_TOPICS: FAQTopic<Topics>[] = [
  {
    id: Topics.howSecure,
    question: 'How do you keep my Cashback Card secure?',
    answer: (
      <>
        We make sure we keep your Cashback Card secure in a number of ways:
        <br />
        <br />
        <ul>
          <li>
            You’ll be given a PIN for your card, which you can change at any
            ATM.
          </li>
          <li>
            We’ll ask you and any additional cardholders to add your mobile
            number to your account, so we can run security checks when you need
            to view your card details online.
          </li>
          <li>
            You can use your UW Cashback Card with Apple Pay or Google Pay,
            which use security features to keep your purchases private and
            secure.
          </li>
          <li>
            As an added security feature, your card does not have a magnetic
            stripe. This helps prevent fraud.{' '}
          </li>
          <li>
            If your card is lost or stolen, you can block it immediately via the
            UW app or your online account.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: Topics.lostCard,
    question: 'What happens if I lose my Cashback Card?',
    answer: (
      <>
        If you lose your Cashback Card, you can easily block it on the UW app or
        your online account. You can either unblock it when you find it, or we
        can issue you with a replacement card.
      </>
    ),
  },
];

const faqSections = [
  {
    id: Sections.orderingCard,
    title: 'Ordering my card',
    topics: ORDERING_TOPICS,
  },
  {
    id: Sections.spending,
    title: 'Spending with my card',
    topics: SPENDING_TOPICS,
  },
  {
    id: Sections.earnings,
    title: 'My Cashback Card earnings',
    topics: EARNINGS_TOPICS,
  },
  {
    id: Sections.security,
    title: 'Security',
    topics: SECURITY_TOPICS,
  },
];

export { faqSections };
